import { EOrderSide } from 'src/types';
import { AnchorPrice, DemoSymbol, FALLBACK_ANCHORS, resolveAnchors } from './priceAnchors';
import { resolveBlockAnchor } from './blockChain';
import { RATE_CONFIG, fixedCurveMultiplier } from './rateBands';
import { clamp, gaussian, isActiveHours, logNormalFromMedianP95, ouStep, poissonInterval, tidyUsd } from './random';
import { getDemoRollingMaturities } from './rollingMaturities';

export interface DemoToken {
  tokenId: number;
  code: DemoSymbol;
  name: string;
  decimals: number;
  price: string;
}

export interface DemoMarket {
  marketId: number;
  tokenId: number;
  code: DemoSymbol;
  name: string;
  instrumentId: string;
  isFloating: boolean;
  daysToMaturity?: number;
  maturityDate?: number;
  // Short rolling-date label for fixed markets (e.g. "1D", "2W", "3M", "4Q").
  // Used by the historical-rates chart to key data by column.
  rollingLabel?: string;
}

interface OrderBookLevel {
  rate: string;
  quantity: string;
}

interface MarketState {
  market: DemoMarket;
  lendRate: number;
  borrowRate: number;
  midRate: number;
  bids: OrderBookLevel[];
  asks: OrderBookLevel[];
  recentTrades: Array<{ s: number; p: string; q: string; d: number }>;
  volume24Usd: number;
  lendDepositsTokens: number;
  borrowDepositsTokens: number;
  nextTradeAt: number;
}

type TopicEventType = 'recentTrades' | 'orderBook' | 'marketInfo' | 'externalMarketInfo';
type Listener = (frame: string) => void;

const TOKEN_DEFS: Array<Omit<DemoToken, 'price'>> = [
  { tokenId: 1, code: 'ETH',  name: 'Ether',           decimals: 18 },
  { tokenId: 2, code: 'USDC', name: 'USD Coin',        decimals: 6  },
  { tokenId: 3, code: 'USDT', name: 'Tether',          decimals: 6  },
  { tokenId: 4, code: 'DAI',  name: 'Dai Stablecoin',  decimals: 18 },
  { tokenId: 5, code: 'WBTC', name: 'Wrapped Bitcoin', decimals: 8  },
];

const TRADE_TICK_MS = 500;
const BOOK_TICK_MS = 250;
const MARKET_INFO_TICK_MS = 2000;

class DemoEngine {
  private anchors: Record<DemoSymbol, AnchorPrice> = FALLBACK_ANCHORS;
  private tokens: DemoToken[] = [];
  private markets: DemoMarket[] = [];
  private state = new Map<number, MarketState>();
  private listeners = new Map<string, Set<Listener>>();
  private started = false;
  private timers: ReturnType<typeof setInterval>[] = [];
  private orderIdSeq = 1_000_000;
  private bootPromise: Promise<void> | null = null;

  boot(): Promise<void> {
    if (this.bootPromise) return this.bootPromise;
    this.bootPromise = (async () => {
      const [anchors] = await Promise.all([resolveAnchors(), resolveBlockAnchor()]);
      this.anchors = anchors;
      this.buildTokensAndMarkets();
      this.initAllMarketState();
      this.startTimers();
      this.started = true;
      console.info(
        '%c INFINITY %c DEMO %c Market data, order books, and trades are synthetic',
        'background:#000;color:#fff;font-weight:600;padding:2px 6px;border-radius:3px 0 0 3px;',
        'background:#f59e0b;color:#000;font-weight:600;padding:2px 6px;border-radius:0 3px 3px 0;',
        'color:inherit;'
      );
    })();
    return this.bootPromise;
  }

  isStarted(): boolean {
    return this.started;
  }

  getTokens(): DemoToken[] {
    return this.tokens.map(t => ({ ...t }));
  }

  getMarkets(): DemoMarket[] {
    return this.markets.map(m => ({ ...m }));
  }

  getMarketState(marketId: number): MarketState | undefined {
    return this.state.get(marketId);
  }

  subscribe(topic: string, fn: Listener): () => void {
    const set = this.listeners.get(topic) ?? new Set();
    set.add(fn);
    this.listeners.set(topic, set);
    return () => {
      set.delete(fn);
    };
  }

  private buildTokensAndMarkets() {
    this.tokens = TOKEN_DEFS.map(t => ({
      ...t,
      price: this.anchors[t.code].anchorUsd.toFixed(t.code === 'WBTC' ? 2 : 4),
    }));
    const markets: DemoMarket[] = [];
    let marketId = 10;
    // One fixed market per rolling-date column on the Markets page so every
    // header (1D..4Q) gets a populated cell.
    const rollingMaturities = getDemoRollingMaturities();
    for (const t of TOKEN_DEFS) {
      markets.push({
        marketId: ++marketId,
        tokenId: t.tokenId,
        code: t.code,
        name: `${t.code} FL`,
        instrumentId: `${t.code}-FL`,
        isFloating: true,
      });
      for (const rm of rollingMaturities) {
        markets.push({
          marketId: ++marketId,
          tokenId: t.tokenId,
          code: t.code,
          name: `${t.code} ${rm.label}`,
          instrumentId: `${t.code}-${rm.label}`,
          isFloating: false,
          daysToMaturity: rm.daysTo,
          maturityDate: rm.date.valueOf(),
          rollingLabel: rm.label,
        });
      }
    }
    this.markets = markets;
  }

  private initAllMarketState() {
    for (const m of this.markets) {
      const cfg = RATE_CONFIG[m.code];
      const band = m.isFloating ? cfg.floating : cfg.fixed;
      const mult = m.isFloating ? 1 : fixedCurveMultiplier(m.daysToMaturity || 28);
      const lendMid = ((band.lendLow + band.lendHigh) / 2) * mult;
      const borrowMid = ((band.borrowLow + band.borrowHigh) / 2) * mult;
      const midRate = (lendMid + borrowMid) / 2;
      const anchorUsd = this.anchors[m.code].anchorUsd;
      const volume24Usd = cfg.volume24Low + Math.random() * (cfg.volume24High - cfg.volume24Low);
      const tokenPerUsd = 1 / anchorUsd;
      // depositsTokens reflects lenders; borrowsTokens reflects borrowers
      const lendDepositsTokens = (volume24Usd * 5) * tokenPerUsd;
      const borrowDepositsTokens = (volume24Usd * 4) * tokenPerUsd;
      const ms: MarketState = {
        market: m,
        lendRate: lendMid,
        borrowRate: borrowMid,
        midRate,
        bids: [],
        asks: [],
        recentTrades: [],
        volume24Usd,
        lendDepositsTokens,
        borrowDepositsTokens,
        nextTradeAt: Date.now() + poissonInterval(1500),
      };
      this.rebuildOrderBook(ms);
      this.seedRecentTrades(ms, 40);
      this.state.set(m.marketId, ms);
    }
  }

  private rebuildOrderBook(ms: MarketState) {
    const cfg = RATE_CONFIG[ms.market.code];
    const spreadBps = cfg.spreadBpsMin + Math.random() * (cfg.spreadBpsMax - cfg.spreadBpsMin);
    const halfSpread = (spreadBps / 10000) / 2;
    const topBid = ms.midRate - halfSpread;
    const topAsk = ms.midRate + halfSpread;
    const rateStep = Math.max(0.00005, ms.midRate * 0.0005); // 5bps or midRate*5bps floor
    const anchorUsd = this.anchors[ms.market.code].anchorUsd;
    const topSizeUsd = cfg.tradeMedianUsd * cfg.topOfBookSizePct * 4;
    const bids: OrderBookLevel[] = [];
    const asks: OrderBookLevel[] = [];
    for (let i = 0; i < 20; i++) {
      const bidRate = topBid - i * rateStep;
      const askRate = topAsk + i * rateStep;
      const decay = Math.pow(1.15, i);
      const bidSizeUsd = topSizeUsd * decay * (0.7 + Math.random() * 0.6);
      const askSizeUsd = topSizeUsd * decay * (0.7 + Math.random() * 0.6);
      bids.push({ rate: bidRate.toFixed(6), quantity: (bidSizeUsd / anchorUsd).toFixed(6) });
      asks.push({ rate: askRate.toFixed(6), quantity: (askSizeUsd / anchorUsd).toFixed(6) });
    }
    ms.bids = bids;
    ms.asks = asks;
  }

  private mutateOrderBook(ms: MarketState) {
    const cfg = RATE_CONFIG[ms.market.code];
    const anchorUsd = this.anchors[ms.market.code].anchorUsd;
    const topSizeUsd = cfg.tradeMedianUsd * cfg.topOfBookSizePct * 4;
    // mutate 1-3 levels per side
    const hits = 1 + Math.floor(Math.random() * 3);
    for (let h = 0; h < hits; h++) {
      const side = Math.random() < 0.5 ? ms.bids : ms.asks;
      const idx = Math.floor(Math.random() * side.length);
      const decay = Math.pow(1.15, idx);
      const sizeUsd = topSizeUsd * decay * (0.7 + Math.random() * 0.6);
      side[idx] = { ...side[idx], quantity: (sizeUsd / anchorUsd).toFixed(6) };
    }
    // slight rate shift on top level to track midRate
    const halfSpread = (cfg.spreadBpsMin / 10000) / 2;
    ms.bids[0] = { ...ms.bids[0], rate: (ms.midRate - halfSpread).toFixed(6) };
    ms.asks[0] = { ...ms.asks[0], rate: (ms.midRate + halfSpread).toFixed(6) };
  }

  private seedRecentTrades(ms: MarketState, n: number) {
    const now = Date.now();
    for (let i = n - 1; i >= 0; i--) {
      const t = this.generateTrade(ms);
      // walk back timestamps so the initial tape has believable history
      t.d = now - i * 4000 - Math.floor(Math.random() * 2000);
      ms.recentTrades.push(t);
    }
    ms.recentTrades.sort((a, b) => b.d - a.d);
  }

  private generateTrade(ms: MarketState) {
    const cfg = RATE_CONFIG[ms.market.code];
    const anchorUsd = this.anchors[ms.market.code].anchorUsd;
    const side: EOrderSide = Math.random() < 0.5 ? EOrderSide.LEND : EOrderSide.BORROW;
    // occasional whale
    const isWhale = Math.random() < 1 / 500;
    const notionalUsd = isWhale
      ? cfg.whaleUsd * (1 + Math.random())
      : logNormalFromMedianP95(cfg.tradeMedianUsd, cfg.tradeP95Usd);
    const usd = tidyUsd(notionalUsd);
    const quantityTokens = usd / anchorUsd;
    const rate = side === EOrderSide.LEND ? ms.lendRate : ms.borrowRate;
    return {
      s: side,
      p: rate.toFixed(6),
      q: quantityTokens.toFixed(6),
      d: Date.now(),
    };
  }

  private stepRates(ms: MarketState) {
    const cfg = RATE_CONFIG[ms.market.code];
    const band = ms.market.isFloating ? cfg.floating : cfg.fixed;
    const mult = ms.market.isFloating ? 1 : fixedCurveMultiplier(ms.market.daysToMaturity || 28);
    const lendMid = ((band.lendLow + band.lendHigh) / 2) * mult;
    const borrowMid = ((band.borrowLow + band.borrowHigh) / 2) * mult;
    const sigma = 0.0005; // ~5bps per tick
    const kappa = 0.05;
    ms.lendRate = clamp(ouStep(ms.lendRate, lendMid, kappa, sigma), band.lendLow * mult, band.lendHigh * mult);
    ms.borrowRate = clamp(ouStep(ms.borrowRate, borrowMid, kappa, sigma), band.borrowLow * mult, band.borrowHigh * mult);
    // enforce invariant: borrow > lend
    if (ms.borrowRate <= ms.lendRate) ms.borrowRate = ms.lendRate + 0.0005;
    ms.midRate = (ms.lendRate + ms.borrowRate) / 2;
  }

  private emit(topic: string, frame: string) {
    const set = this.listeners.get(topic);
    if (!set) return;
    set.forEach(fn => {
      try { fn(frame); } catch (e) { console.error('[demoEngine] listener threw for topic', topic, e); }
    });
  }

  private buildWsFrame(eventType: TopicEventType, ms: MarketState, payload: any): string {
    return JSON.stringify({
      e: eventType,
      E: Date.now(),
      s: ms.market.instrumentId,
      m: ms.market.marketId,
      P: payload,
    });
  }

  private tickTrades() {
    const now = Date.now();
    const activity = isActiveHours(now) ? 0.6 : 1.0;
    for (const ms of this.state.values()) {
      if (now < ms.nextTradeAt) continue;
      this.stepRates(ms);
      const trade = this.generateTrade(ms);
      ms.recentTrades.unshift(trade);
      if (ms.recentTrades.length > 200) ms.recentTrades.length = 200;
      const anchorUsd = this.anchors[ms.market.code].anchorUsd;
      ms.volume24Usd += Number(trade.q) * anchorUsd;
      const topic = `${ms.market.instrumentId}@recentTrades`;
      this.emit(topic, this.buildWsFrame('recentTrades', ms, [trade]));
      const mean = 1500 * activity * (0.6 + Math.random() * 0.8);
      ms.nextTradeAt = now + poissonInterval(mean);
    }
  }

  private tickBooks() {
    for (const ms of this.state.values()) {
      this.mutateOrderBook(ms);
      const topic = `${ms.market.instrumentId}@orderBook`;
      const payload = {
        a: ms.asks.map(e => ({ p: e.rate, q: e.quantity })),
        b: ms.bids.map(e => ({ p: e.rate, q: e.quantity })),
      };
      this.emit(topic, this.buildWsFrame('orderBook', ms, payload));
    }
  }

  private tickMarketInfo() {
    for (const ms of this.state.values()) {
      const topic = `${ms.market.instrumentId}@marketInfo`;
      // gentle volume drift
      const drift = gaussian(0, ms.volume24Usd * 0.005);
      ms.volume24Usd = Math.max(1000, ms.volume24Usd + drift);
      const anchorUsd = this.anchors[ms.market.code].anchorUsd;
      const payload = {
        m: ms.market.marketId,
        p: ms.midRate.toFixed(6),
        d: ms.midRate >= ms.lendRate,
        pd: Date.now(),
        b: (ms.borrowDepositsTokens * anchorUsd).toFixed(2),
        l: (ms.lendDepositsTokens * anchorUsd).toFixed(2),
        M: ms.midRate.toFixed(6),
      };
      this.emit(topic, this.buildWsFrame('marketInfo', ms, payload));
    }
  }

  private startTimers() {
    this.timers.push(setInterval(() => this.tickTrades(), TRADE_TICK_MS));
    this.timers.push(setInterval(() => this.tickBooks(), BOOK_TICK_MS));
    this.timers.push(setInterval(() => this.tickMarketInfo(), MARKET_INFO_TICK_MS));
  }

  nextOrderId(): number {
    return this.orderIdSeq++;
  }
}

export const demoEngine = new DemoEngine();
